import { collection, query, orderBy, getDocs, setDoc, deleteDoc, doc } from 'firebase/firestore';
import { getDb } from './firebase';
import type { MapLayer } from '../types';

const COLLECTION_NAME = 'map_layers';

/** Recursively remove undefined values so Firestore doesn't reject the write. */
function stripUndefined(obj: Record<string, any>): Record<string, any> {
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => [
        k,
        v !== null && typeof v === 'object' && !Array.isArray(v)
          ? stripUndefined(v as Record<string, any>)
          : v,
      ])
  );
}

/**
 * List all map layers ordered by their display order.
 */
export async function listMapLayers(): Promise<MapLayer[]> {
  const q = query(collection(getDb(), COLLECTION_NAME), orderBy('order', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }) as MapLayer);
}

/**
 * Create a new map layer. Uses the layer's id as the document ID.
 */
export async function createMapLayer(layer: MapLayer): Promise<void> {
  await setDoc(
    doc(getDb(), COLLECTION_NAME, layer.id),
    stripUndefined(layer as unknown as Record<string, any>)
  );
}

/**
 * Update an existing map layer. Overwrites the full document.
 */
export async function updateMapLayer(layerId: string, data: Partial<MapLayer>): Promise<void> {
  await setDoc(doc(getDb(), COLLECTION_NAME, layerId), stripUndefined(data as Record<string, any>));
}

/**
 * Persist a new order for all layers after a drag-and-drop reorder.
 * Writes each layer's updated `order` value.
 */
export async function reorderMapLayers(layers: MapLayer[]): Promise<void> {
  await Promise.all(
    layers.map((layer, index) =>
      setDoc(doc(getDb(), COLLECTION_NAME, layer.id), { order: index + 1 }, { merge: true })
    )
  );
}

/**
 * Delete a map layer.
 */
export async function deleteMapLayer(layerId: string): Promise<void> {
  await deleteDoc(doc(getDb(), COLLECTION_NAME, layerId));
}
